// modules/reporteFacturas/pages/ReporteFacturasPorAnioMesPage.jsx
// Migracion de reporte_factura_anual.php ("Reporte por Año y Mes") —
// compara, mes a mes, cuanto se pre-facturo vs se facturo (solo servicios
// de Alquiler, igual que el PHP legado) y cuanto de eso ya se cobro. Sin
// filtro de sucursal — el original tampoco lo tenia para este reporte.
//
// Se le arreglaron dos cosas del PHP viejo:
//   - La consulta de fecha usaba SUBSTRING/SUBSTRING_INDEX sobre el string
//     (fragil); ahora usa YEAR()/MONTH() nativos (ver reporteFacturasModel).
//   - Habia un bug real: la columna "Cobrado Factura" de diciembre sumaba
//     por error a la variable de Pre-Factura de diciembre — con la logica
//     en arrays esto ya no puede pasar.
//
// El detalle de una celda no se re-consulta por fecha/categoria: el backend
// ya manda la lista exacta de `cod` de cada celda junto con el total, y al
// hacer click se piden esos codigos puntuales (mismo patron que el AJAX a
// cargaDetalle.php del sistema viejo).
import React, { useState } from "react";
import {
  Box, Button, TextField, MenuItem, Typography, Alert, CircularProgress,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/layout/PageHeader";
import { getReportePorAnioMes, getDetallePorCodigos } from "../services/reporteFacturasService";

const StyledHeaderCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  fontSize: "0.72rem",
  whiteSpace: "nowrap",
  padding: "3px 6px",
  backgroundColor: theme.palette.mode === "dark"
    ? theme.palette.grey[800] : theme.palette.grey[100],
}));

const compactFieldSx = {
  "& .MuiInputBase-root": { minHeight: 32, fontSize: "0.8rem" },
  "& .MuiInputBase-input": { py: 0.65 },
  "& .MuiInputLabel-root": { fontSize: "0.78rem" },
  "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
};

const NOMBRES_MES_CORTOS = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

const anioActual = new Date().getFullYear();
const ANIOS = Array.from({ length: 12 }, (_, i) => anioActual + 1 - i);

const formatMoneda = (value) =>
  Number(value || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatFecha = (value) => {
  const m = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return "-";
  return `${m[3]}/${m[2]}/${m[1]}`;
};

const CeldaMonto = ({ valor, cods, onClick }) => (
  <TableCell align="right">
    {Number(valor) > 0 && cods?.length ? (
      <Button
        variant="text" size="small"
        onClick={onClick}
        sx={{ p: 0, minWidth: 0, minHeight: 0, fontSize: "0.74rem" }}
      >
        {formatMoneda(valor)}
      </Button>
    ) : formatMoneda(valor)}
  </TableCell>
);

const ReporteFacturasPorAnioMesPage = () => {
  const [year, setYear] = useState(anioActual);
  const [data, setData] = useState(null);
  const [buscado, setBuscado] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [detalle, setDetalle] = useState(null);
  const [detalleTitulo, setDetalleTitulo] = useState(null);
  const [cargandoDetalle, setCargandoDetalle] = useState(false);
  const [errorDetalle, setErrorDetalle] = useState("");

  const handleBuscar = async () => {
    setDetalle(null);
    setDetalleTitulo(null);
    setLoading(true);
    setError("");
    try {
      const { data: resp } = await getReportePorAnioMes({ year });
      setData(resp);
      setBuscado(true);
    } catch (e) {
      setData(null);
      setError(e?.response?.data?.error || "No se pudo generar el reporte.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerDetalle = async (titulo, cods) => {
    setDetalleTitulo(titulo);
    setCargandoDetalle(true);
    setErrorDetalle("");
    try {
      const { data: resp } = await getDetallePorCodigos(cods);
      setDetalle(resp);
    } catch (e) {
      setDetalle(null);
      setErrorDetalle(e?.response?.data?.error || "No se pudo cargar el detalle.");
    } finally {
      setCargandoDetalle(false);
    }
  };

  const handleCerrarDetalle = () => {
    setDetalleTitulo(null);
    setDetalle(null);
    setErrorDetalle("");
  };

  const categorias = data?.categorias || [];

  return (
    <DashboardLayout>
      <PageHeader
        title="Reporte por Año y Mes"
        subtitle="Totales pre facturados, facturados y cobrados por mes"
        backTo="/reporte-facturas"
      />
      <Box sx={{ p: 1 }}>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 2, flexWrap: "wrap" }}>
          <TextField
            select size="small" label="Año"
            sx={{ ...compactFieldSx, width: 120 }}
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
          >
            {ANIOS.map((a) => (
              <MenuItem key={a} value={a} sx={{ fontSize: "0.85rem" }}>{a}</MenuItem>
            ))}
          </TextField>

          <Button
            size="small"
            variant="contained"
            startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <SearchIcon />}
            onClick={handleBuscar}
            disabled={loading}
            sx={{ minHeight: 32 }}
          >
            {loading ? "Buscando..." : "Buscar"}
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {!buscado ? (
          <Alert severity="info">Elige un año y presiona Buscar. Solo incluye facturas de Alquiler.</Alert>
        ) : (
          <>
            <Paper variant="outlined">
              <TableContainer>
                <Table size="small" sx={{ "& td": { py: 0.35, px: 0.75, fontSize: "0.74rem", whiteSpace: "nowrap" } }}>
                  <TableHead>
                    <TableRow>
                      <StyledHeaderCell>Categoría</StyledHeaderCell>
                      {NOMBRES_MES_CORTOS.map((m) => (
                        <StyledHeaderCell key={m} align="right">{m}</StyledHeaderCell>
                      ))}
                      <StyledHeaderCell align="right">Total</StyledHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={14} align="center" sx={{ py: 3 }}>
                          <CircularProgress size={24} />
                        </TableCell>
                      </TableRow>
                    ) : (
                      categorias.map((c) => (
                        <TableRow key={c.key} hover>
                          <TableCell sx={{ fontWeight: 600 }}>{c.label}</TableCell>
                          {(data?.meses || []).map((m) => {
                            const celda = m[c.key] || { monto: 0, cods: [] };
                            return (
                              <CeldaMonto
                                key={m.mes}
                                valor={celda.monto}
                                cods={celda.cods}
                                onClick={() => handleVerDetalle(`${c.label} — ${m.nombre} ${year}`, celda.cods)}
                              />
                            );
                          })}
                          <CeldaMonto
                            valor={data.totales[c.key]?.monto}
                            cods={data.totales[c.key]?.cods}
                            onClick={() => handleVerDetalle(`${c.label} — Total ${year}`, data.totales[c.key]?.cods)}
                          />
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            {detalleTitulo && (
              <Paper variant="outlined" sx={{ mt: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Detalle — {detalleTitulo}
                  </Typography>
                  <IconButton size="small" onClick={handleCerrarDetalle}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>

                {errorDetalle && <Alert severity="error" sx={{ m: 1.5 }}>{errorDetalle}</Alert>}

                {cargandoDetalle ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : detalle && (
                  <>
                    <TableContainer sx={{ maxHeight: "50vh" }}>
                      <Table size="small" stickyHeader sx={{ "& td": { py: 0.35, px: 0.75, fontSize: "0.74rem", whiteSpace: "nowrap" } }}>
                        <TableHead>
                          <TableRow>
                            <StyledHeaderCell>Cliente</StyledHeaderCell>
                            <StyledHeaderCell>Grupo</StyledHeaderCell>
                            <StyledHeaderCell>Nombre</StyledHeaderCell>
                            <StyledHeaderCell>Descripción</StyledHeaderCell>
                            <StyledHeaderCell>Código Servicio</StyledHeaderCell>
                            <StyledHeaderCell>Tipo</StyledHeaderCell>
                            <StyledHeaderCell>N° Factura</StyledHeaderCell>
                            <StyledHeaderCell>Fecha Factura</StyledHeaderCell>
                            <StyledHeaderCell>Fecha Emisión</StyledHeaderCell>
                            <StyledHeaderCell>Mes de Servicio</StyledHeaderCell>
                            <StyledHeaderCell align="right">Monto Bolivianos</StyledHeaderCell>
                            <StyledHeaderCell align="right">Monto Dólares</StyledHeaderCell>
                            <StyledHeaderCell align="right">Saldo</StyledHeaderCell>
                            <StyledHeaderCell>Cobrada</StyledHeaderCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {detalle.items.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={14} align="center" sx={{ py: 2, fontStyle: "italic", color: "text.secondary" }}>
                                Sin facturas.
                              </TableCell>
                            </TableRow>
                          ) : (
                            detalle.items.map((it, idx) => (
                              <TableRow key={idx} hover>
                                <TableCell>{it.nombre}</TableCell>
                                <TableCell>{it.grupo || "-"}</TableCell>
                                <TableCell>{it.nombre_servicio || "-"}</TableCell>
                                <TableCell>{it.descrip || "-"}</TableCell>
                                <TableCell>{it.cod_serv}</TableCell>
                                <TableCell>{it.tipo}</TableCell>
                                <TableCell>{it.num_fac}</TableCell>
                                <TableCell>{formatFecha(it.fecha_fac)}</TableCell>
                                <TableCell>{formatFecha(it.fecha_emi)}</TableCell>
                                <TableCell>{it.mes_servicio}</TableCell>
                                <TableCell align="right">{formatMoneda(it.bolivianos)}</TableCell>
                                <TableCell align="right">{formatMoneda(it.dolares)}</TableCell>
                                <TableCell align="right">{formatMoneda(it.saldo)}</TableCell>
                                <TableCell>{it.pagada}</TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <Box sx={{ display: "flex", gap: 3, justifyContent: "flex-end", p: 1.5, borderTop: "1px solid", borderColor: "divider" }}>
                      <Typography variant="body2">Total Bolivianos: <b>{formatMoneda(detalle.totales.bolivianos)}</b></Typography>
                      <Typography variant="body2">Total Dólares: <b>{formatMoneda(detalle.totales.dolares)}</b></Typography>
                    </Box>
                  </>
                )}
              </Paper>
            )}
          </>
        )}
      </Box>
    </DashboardLayout>
  );
};

export default ReporteFacturasPorAnioMesPage;
